// sentenceBuffer.ts
// Accumulates transcription words and emits complete sentences

export interface TranscriptSegment {
  text: string;
  speaker: string;      // "Speaker 1", "Speaker 2"
  timestamp: number;
  isFinal: boolean;
}

export interface BufferedSentence {
  text: string;
  speaker: string;
  startTimestamp: number;
  endTimestamp: number;
  wordCount: number;
}

export interface SentenceBufferConfig {
  minWordsBeforeFlush: number;      // Default: 10
  maxWordsBeforeForceFlush: number; // Default: 60
  sentenceEndPatterns: RegExp;      // Default: /[.!?]\s*$/
  flushOnSpeakerChange: boolean;    // Default: true
  debounceMs: number;               // Default: 800
}

const DEFAULT_CONFIG: SentenceBufferConfig = {
  minWordsBeforeFlush: 10,
  maxWordsBeforeForceFlush: 60,
  sentenceEndPatterns: /[.!?]\s*$/,
  flushOnSpeakerChange: true,
  debounceMs: 800,
};

export class SentenceBuffer {
  private config: SentenceBufferConfig;
  private onSentenceComplete: (sentence: BufferedSentence) => void;

  private buffer: string = '';
  private currentSpeaker: string = '';
  private startTimestamp: number = 0;
  private lastTimestamp: number = 0;
  private debounceTimer: NodeJS.Timeout | null = null;

  constructor(
    onSentenceComplete: (sentence: BufferedSentence) => void,
    config: Partial<SentenceBufferConfig> = {}
  ) {
    this.onSentenceComplete = onSentenceComplete;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  private getWordCount(text: string): number {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  }

  private clearDebounceTimer(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  private createSentence(): BufferedSentence | null {
    const text = this.buffer.trim();
    if (!text) return null;

    const sentence: BufferedSentence = {
      text,
      speaker: this.currentSpeaker,
      startTimestamp: this.startTimestamp,
      endTimestamp: this.lastTimestamp,
      wordCount: this.getWordCount(text),
    };

    // Reset buffer
    this.buffer = '';
    this.startTimestamp = 0;

    return sentence;
  }

  private emitSentence(): void {
    this.clearDebounceTimer();
    const sentence = this.createSentence();
    if (sentence) {
      this.onSentenceComplete(sentence);
    }
  }

  private scheduleDebounceFlush(): void {
    this.clearDebounceTimer();
    this.debounceTimer = setTimeout(() => {
      const wordCount = this.getWordCount(this.buffer);
      if (wordCount >= this.config.minWordsBeforeFlush) {
        this.emitSentence();
      }
    }, this.config.debounceMs);
  }

  addSegment(segment: TranscriptSegment): void {
    const { text, speaker, timestamp, isFinal } = segment;

    // Handle speaker change - flush immediately if enabled
    if (this.config.flushOnSpeakerChange &&
        this.currentSpeaker &&
        this.currentSpeaker !== speaker &&
        this.buffer.trim()) {
      this.emitSentence();
    }

    // Update speaker
    if (!this.currentSpeaker || this.currentSpeaker !== speaker) {
      this.currentSpeaker = speaker;
    }

    // Set start timestamp if this is first content in buffer
    if (!this.startTimestamp) {
      this.startTimestamp = timestamp;
    }
    this.lastTimestamp = timestamp;

    // Add text to buffer
    if (this.buffer && !this.buffer.endsWith(' ') && !text.startsWith(' ')) {
      this.buffer += ' ';
    }
    this.buffer += text;

    const wordCount = this.getWordCount(this.buffer);

    // Check flush conditions
    // 1. Force flush if word count >= maxWordsBeforeForceFlush
    if (wordCount >= this.config.maxWordsBeforeForceFlush) {
      this.emitSentence();
      return;
    }

    // 2. Flush if word count >= minWordsBeforeFlush AND ends with sentence-ending punctuation
    if (wordCount >= this.config.minWordsBeforeFlush &&
        this.config.sentenceEndPatterns.test(this.buffer.trim())) {
      this.emitSentence();
      return;
    }

    // 3. Schedule debounce flush if isFinal and have enough words
    if (isFinal && wordCount >= this.config.minWordsBeforeFlush) {
      this.scheduleDebounceFlush();
    }
  }

  flush(): BufferedSentence | null {
    this.clearDebounceTimer();
    const sentence = this.createSentence();
    if (sentence) {
      this.onSentenceComplete(sentence);
    }
    return sentence;
  }

  forceFlush(): BufferedSentence | null {
    this.clearDebounceTimer();
    return this.createSentence();
  }

  clear(): void {
    this.clearDebounceTimer();
    this.buffer = '';
    this.currentSpeaker = '';
    this.startTimestamp = 0;
    this.lastTimestamp = 0;
  }

  getBufferState(): { text: string; wordCount: number; speaker: string } {
    return {
      text: this.buffer,
      wordCount: this.getWordCount(this.buffer),
      speaker: this.currentSpeaker,
    };
  }

  updateConfig(config: Partial<SentenceBufferConfig>): void {
    this.config = { ...this.config, ...config };
  }
}
